import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import tw, { styled } from 'twin.macro';
import ReactSelect from 'react-select';
import { useTranslation } from 'react-i18next';
import chroma from 'chroma-js';

import { Flex } from '@sopranodesign/pulse-components';

import BurgerButton from '../BurgerButton';
import SendButton from '../SendButton';
import BaseComposer from '../BaseComposer';
import {
  useComposer,
  useDelivery,
  useBot,
  useTheme
} from '../../../../../hooks/index';

const Container = styled(BaseComposer.Container)`
  ${tw`h-16 flex items-center`}
`;

const StyledSelect = styled(ReactSelect)`
  ${tw`w-full h-full flex`}
`;

const Select = props => {
  const { sentMessage } = useTheme();

  const { t } = useTranslation('bots');

  const styleOverrides = {
    control: () => tw`w-full flex items-center`,
    container: () => tw`static px-4`,
    valueContainer: styles => ({
      maxHeight: 36,
      ...styles,
      ...tw`p-0 overflow-y-auto`
    }),
    menu: base => ({
      ...base,
      margin: '0.5rem',
      left: 0,
      width: 'calc(100% - 1rem)'
    })
  };

  const styles = {
    multiValueLabel: styles => ({
      ...styles,
      ...tw`font-poppins uppercase leading-none text-xs`,
      color: sentMessage.text
    }),
    multiValue: styles => ({
      ...styles,
      backgroundColor: chroma(sentMessage.background).alpha(0.8).css(),
      marginTop: -1
    }),
    multiValueRemove: styles => ({ ...styles, color: sentMessage.text }),
    placeholder: styles => ({ ...styles, ...tw`text-gray-500` }),
    option: (styles, state) => {
      return {
        ...styles,
        boxShadow: 'none',
        background: state.isSelected
          ? sentMessage.background
          : state.isFocused
            ? chroma(sentMessage.background).alpha(0.25).css()
            : 'none',
        color:
          state.isSelected || state.isFocuesed ? sentMessage.text : state.color,
        '&:hover': {
          background:
            state.isSelected || state.isFocuesed
              ? sentMessage.background
              : chroma(sentMessage.background).alpha(0.25).css(),
          color:
            state.isSelected || state.isFocuesed ? sentMessage.text : 'black'
        }
      };
    }
  };

  return (
    <StyledSelect
      menuPlacement="top"
      styles={{ ...styles, ...styleOverrides }}
      components={{ IndicatorsContainer: () => null }}
      noOptionsMessage={() => t('fuzzy_composer_no_options_message')}
      {...props}
    />
  );
};

const FuzzyComposer = ({ content }) => {
  const { reply, setReply } = useComposer();
  const { sendMessage } = useDelivery();
  const { showBurgerIcon, setShowBurgerMenu } = useBot();
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const { t } = useTranslation('bots');

  const handleBurgerIconClick = () => {
    setShowBurgerMenu(open => !open);
  };

  const handleSelectChange = selection => {
    if (!selection) {
      return setReply('');
    }

    if (content.multi) {
      return setReply(selection.map(option => option.value));
    }

    setReply(selection.value);
  };

  const handleSubmit = () => {
    if (!reply) {
      return;
    }

    if (!content.multi) {
      const postback = reply;

      const message = content.items.find(option => option.value === postback)
        .label;

      sendMessage(message, postback);
      return setReply('');
    }

    if (reply.length === 1) {
      const postback = reply[0];

      const message = content.items.find(option => option.value === postback)
        .label;

      sendMessage(message, postback);
      return setReply('');
    }

    if (reply.length > 1) {
      const postback = reply;

      const message = postback
        .map(item => content.items.find(option => option.value === item).label)
        .toString()
        .replace(',', ', ');

      sendMessage(message, postback.toString());
      return setReply('');
    }
  };

  /**
   * We want React Select to manage the Input value of the select, until
   * we need to clear it.
   */
  const value = useMemo(() => {
    // Let React Select manage it's own state of the Input.
    if (reply) {
      return undefined;
    }

    // Control the Input so we can clear the value.
    return '';
  }, [reply]);

  const handleOnKeyDown = event => {
    if (isSelectOpen) {
      return null;
    } else if (event.keyCode === 13) {
      handleSubmit();
    }
  };

  return (
    <Container>
      <Flex xSpaceSm middle fat="true" center pl pr tall>
        {showBurgerIcon && (
          <BurgerButton
            aria-label={t('burger_button_label')}
            onClick={handleBurgerIconClick}
          />
        )}
        <Select
          aria-label={content.placeholder}
          placeholder={content.placeholder}
          options={content.items.map(({ label, value }) => ({
            value,
            label
          }))}
          onChange={handleSelectChange}
          handleSubmit={handleSubmit}
          onKeyDown={handleOnKeyDown}
          isMulti={content.multi}
          value={value}
          closeMenuOnSelect={content.multi ? false : true}
          onMenuOpen={() => {
            setIsSelectOpen(true);
          }}
          onMenuClose={() => {
            setIsSelectOpen(false);
          }}
        />
        <SendButton
          disabled={reply.length === 0 ? true : false}
          aria-label={t('send_button_label')}
          onClick={handleSubmit}
        />
      </Flex>
    </Container>
  );
};

FuzzyComposer.propTypes = {
  content: PropTypes.shape({
    placeholder: PropTypes.string,
    multi: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired
  })
};

export default FuzzyComposer;
